import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { isMotionPaused } from '@/lib/motion'
import { TweenNumber } from '@/components/generals/tween-number'

type Metric = 'energy' | 'carbon'

interface RacingBarsProps {
    projects: Array<ProjectSummary>
    run: boolean
    className?: string
}

const ROW = 44
const SORT_DELAY = 650

const METRICS: Record<Metric, { label: string, unit: string, read: (project: ProjectSummary) => number }> = {
    energy: { label: 'kWh per m2', unit: 'kWh/m² a year', read: project => project.energyKwhM2 },
    carbon: { label: 'kgCO2e per m2', unit: 'kgCO2e/m² embodied', read: project => project.carbonKgM2 }
}

const rankOrder = (projects: Array<ProjectSummary>, read: (project: ProjectSummary) => number): Map<string, number> => {
    const order = [...projects].sort((a, b) => read(a) - read(b) || a.name.localeCompare(b.name))

    return new Map(order.map((project, index) => [project.id, index]))
}

/**
 * Six projects ranked on one metric. The bars grow in project order, then the rows slide into rank
 * order from lowest to highest. Switching the metric resizes every bar first and re-sorts after.
 */
export const RacingBars: FC<RacingBarsProps> = ({ projects, run, className }): ReactNode => {
    const [metric, setMetric] = useState<Metric>('energy')
    const read = METRICS[metric].read
    const ranks = useMemo(() => rankOrder(projects, read), [projects, read])
    const listOrder = useMemo(() => new Map(projects.map((project, index) => [project.id, index])), [projects])
    const [placed, setPlaced] = useState<Map<string, number> | null>(null)

    useEffect(() => {
        if (!run) return

        if (isMotionPaused() || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setPlaced(ranks)

            return
        }

        const timer = window.setTimeout(() => setPlaced(ranks), SORT_DELAY)

        return () => window.clearTimeout(timer)
    }, [run, ranks])

    const positions = placed ?? listOrder
    const max = Math.max(1, ...projects.map(read)) * 1.06
    const sorted = placed === ranks

    return (
        <div className={cn('w-full', className)}>
            <div className='mb-6 flex flex-wrap items-center justify-between gap-3'>
                <div role='group' aria-label='Metric' className='flex border border-hairline'>
                    {(Object.keys(METRICS) as Array<Metric>).map(key => (
                        <button
                            key={key}
                            type='button'
                            aria-pressed={metric === key}
                            onClick={() => setMetric(key)}
                            className={cn(
                                'hud h-9 px-3 transition-colors duration-300',
                                metric === key ? 'bg-ink text-background' : 'text-mute hover:text-ink'
                            )}
                        >
                            {METRICS[key].label}
                        </button>
                    ))}
                </div>

                <p className='hud text-mute' aria-live='polite'>
                    {sorted ? 'Sorted / lowest first' : 'Sorting'}
                </p>
            </div>

            <div className='hud grid grid-cols-[3rem_minmax(0,8.5rem)_minmax(0,1fr)_3.5rem] gap-4 border-b border-hairline pb-2 text-mute sm:grid-cols-[3rem_minmax(0,10rem)_minmax(0,1fr)_3.5rem]' aria-hidden='true'>
                <span>
                    Rank
                </span>

                <span>
                    Project
                </span>

                <span />

                <span className='text-right'>
                    Value
                </span>
            </div>

            <ol className='relative' style={{ height: projects.length * ROW }} aria-label={`Projects ranked by ${METRICS[metric].unit}, lowest first`}>
                {projects.map(project => {
                    const rank = ranks.get(project.id) ?? 0
                    const position = positions.get(project.id) ?? 0
                    const value = read(project)

                    return (
                        <li
                            key={project.id}
                            className='race-row absolute inset-x-0 top-0 grid h-11 grid-cols-[3rem_minmax(0,8.5rem)_minmax(0,1fr)_3.5rem] items-center gap-4 border-b border-hairline sm:grid-cols-[3rem_minmax(0,10rem)_minmax(0,1fr)_3.5rem]'
                            style={{ transform: `translate3d(0, ${position * ROW}px, 0)` }}
                        >
                            <span className='flex items-center'>
                                <span className={cn('font-mono text-[10px] tracking-[0.08em] transition-colors duration-500', sorted && rank === 0 ? 'text-signal' : 'text-mute')}>
                                    <span className='sr-only'>
                                        Rank
                                    </span>
                                    #{rank + 1}
                                </span>
                            </span>

                            <span className='truncate text-[14px] text-ink' data-mdlfy-cms={`projects:${project.id}:name`}>
                                {project.name}
                            </span>

                            <span className='relative h-1.5 bg-hairline/60' aria-hidden='true'>
                                <span
                                    className='race-bar absolute inset-y-0 left-0 bg-ink'
                                    style={{ width: run ? `${(value / max) * 100}%` : '0%' }}
                                />
                            </span>

                            <span className='hud text-right text-ink'>
                                <span className='sr-only'>
                                    {value} {METRICS[metric].unit}
                                </span>

                                <span aria-hidden='true'>
                                    <TweenNumber value={run ? value : 0} duration={900} />
                                </span>
                            </span>
                        </li>
                    )
                })}
            </ol>

            <p className='hud mt-4 text-mute'>
                {METRICS[metric].unit} / lower is better
            </p>
        </div>
    )
}
